import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, inject } from '@angular/core';

import type { Day } from '../rental-billing.model';
import { PickerMarks, PickerSel, pickerClick, pickerDayFlags, pickerKey, pickerMonthOf } from '../rental-picker.geometry';
import { addDays, addMonthsClamped, firstOfMonth, frMonth, frWeekday } from '../rental-days';

export type PickerPhase = 'start' | 'end';
const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];
const FLAGS = ['st', 'solo', 'inr', 'open', 'en'] as const;

interface PickerCell { d: Day; num: number; cls: string; disabled: boolean; tip: string; aria: string; pressed: boolean; today: boolean; lock: boolean; dot: boolean; tab: 0 | -1; }
interface PickerMonthView { first: Day; title: string; blanks: number[]; cells: PickerCell[]; }

// Calendrier de deux mois, semaine du lundi ; sous-composant de dialogue : il n'injecte aucun service de dialogue (E.5).
// En mode plage, le 1er clic pose start et le 2e le DERNIER jour (end, inclus ; le début lui-même = un seul jour) : plus de jour de reprise exclu depuis le 27/10/2026.
@Component({
  selector: 'app-rental-day-picker',
  templateUrl: './rental-day-picker.component.html',
  styleUrls: ['./rental-day-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  host: { '[attr.data-tone]': 'tone', '[attr.data-months]': 'monthCount', '[attr.data-six-weeks]': 'sixWeeks || null', '[attr.data-dense]': 'dense || null' },
})
export class RentalDayPickerComponent implements OnChanges {
  @Input() mode: 'range' | 'single' = 'range';
  @Input() start: Day | null = null;
  @Input() end: Day | null = null; // dernier jour de la plage, inclus
  @Input() startFloor: Day | null = null; // affichage de start null (« dès la livraison »)
  @Input() leftMonth!: Day; // 1er du mois de gauche
  @Input() monthCount: 2 | 3 | 4 = 2; // mois affichés côte à côte
  @Input() sixWeeks = false; // vrai : place de six semaines toujours tenue, la hauteur ne change pas d'un mois à l'autre (JP 29/10)
  @Input() dense = false; // vrai : semaines de 30 px au lieu de 38, pour un dialogue qui doit tenir en hauteur (Prescriptions, JP 03/11)
  @Input() today!: Day; // as_of
  @Input() marks: PickerMarks | null = null;
  @Input() tone: 'amber' | 'violet' | 'teal' | 'slate' | 'orange' = 'amber';
  @Input() captureDay = false; // vrai : un clic émet dayPick sans toucher à la sélection (jour tenu par l'hôte, ex. date de l'ordonnance)
  @Input() ringDay: Day | null = null; // anneau bleu sur ce jour (date de l'ordonnance)
  // Ajout compatible : phase tenue par l'hôte ([(phase)]) quand il pilote les champs ; null = tenue ici, déduite de start et end.
  @Input() phase: PickerPhase | null = null;
  @Output() startChange = new EventEmitter<Day | null>();
  @Output() endChange = new EventEmitter<Day | null>();
  @Output() leftMonthChange = new EventEmitter<Day>();
  @Output() escape = new EventEmitter<void>(); // Échap sans sélection
  @Output() phaseChange = new EventEmitter<PickerPhase>();
  @Output() dayPick = new EventEmitter<Day>();

  readonly weekdays = WEEKDAYS;
  months: PickerMonthView[] = [];

  private cdr = inject(ChangeDetectorRef);
  private host: HTMLElement = inject(ElementRef).nativeElement;
  private left: Day | null = null;
  private cur: { start: Day | null; end: Day | null } = { start: null, end: null };
  private ownPhase: PickerPhase = 'start';
  private hover: Day | null = null;
  private focusDay: Day | null = null;

  constructor() {
    // Survol hors zone : seul le calendrier est revérifié, et seulement quand le jour survolé change.
    const over = (ev: Event) => this.onOver(ev);
    const leave = () => this.onLeave();
    inject(NgZone).runOutsideAngular(() => {
      this.host.addEventListener('pointerover', over);
      this.host.addEventListener('pointerleave', leave);
    });
    inject(DestroyRef).onDestroy(() => {
      this.host.removeEventListener('pointerover', over);
      this.host.removeEventListener('pointerleave', leave);
    });
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['leftMonth'] && this.leftMonth) this.left = firstOfMonth(this.leftMonth);
    if (ch['start'] || ch['end']) {
      this.cur = { start: this.start, end: this.end };
      this.hover = null;
    }
    if ((ch['start'] || ch['end'] || ch['startFloor'] || ch['mode']) && this.phase === null) {
      this.ownPhase = this.mode === 'range' && this.cur.end === null && this.startEff() !== null ? 'end' : 'start';
    }
    this.rebuild();
  }

  select(d: Day): void {
    if (this.isDisabled(d)) return;
    this.focusDay = d;
    if (this.captureDay) {
      this.dayPick.emit(d);
      return;
    }
    if (this.mode === 'single') {
      this.cur = { ...this.cur, start: d };
      this.startChange.emit(d);
      this.rebuild();
      return;
    }
    const r = pickerClick(d, this.sel());
    this.hover = null;
    if (r.sel.phase === 'end') {
      const hadEnd = this.cur.end !== null;
      this.cur = { start: d, end: null };
      this.startChange.emit(d);
      if (hadEnd) this.endChange.emit(null);
      this.setPhase('end');
    } else {
      this.cur = { ...this.cur, end: d };
      this.endChange.emit(d);
      this.setPhase('start');
    }
    this.rebuild();
  }

  shift(months: number): void {
    this.setLeft(addMonthsClamped(this.currentLeft(), months));
    this.rebuild();
  }

  onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation(); // le dialogue hôte ne doit pas traiter le même Échap
      this.clearOrEscape();
      return;
    }
    const d = (ev.target as HTMLElement | null)?.closest?.('[data-day]')?.getAttribute('data-day') ?? null;
    if (!d) return;
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this.select(d);
      return;
    }
    const next = this.step(d, ev.key);
    if (next === undefined) return;
    ev.preventDefault();
    if (next !== null) this.moveFocus(next);
  }

  private clearOrEscape(): void {
    if (this.startEff() === null && this.cur.end === null) {
      this.escape.emit();
      return;
    }
    this.cur = { start: null, end: null };
    this.hover = null;
    this.startChange.emit(null);
    this.endChange.emit(null);
    this.setPhase('start');
    this.rebuild();
  }

  // Jour suivant au clavier ; un jour grisé n'est pas focalisable, on poursuit dans le même sens (undefined : touche sans objet).
  private step(d: Day, key: string): Day | null | undefined {
    let next = pickerKey(d, key);
    if (next === null) return undefined;
    const inc = key === 'PageUp' ? -1 : key === 'PageDown' ? 1 : null;
    for (let guard = 0; this.isDisabled(next) && guard < 62; guard++) {
      next = inc === null ? pickerKey(next, key)! : addDays(next, inc);
    }
    return this.isDisabled(next) ? null : next;
  }

  private moveFocus(d: Day): void {
    this.focusDay = d;
    if (this.mode === 'range' && this.ph() === 'end') this.hover = d;
    const left = this.currentLeft();
    if (d < left) this.setLeft(firstOfMonth(d));
    else if (d >= addMonthsClamped(left, this.monthCount)) this.setLeft(addMonthsClamped(firstOfMonth(d), 1 - this.monthCount));
    this.rebuild();
    this.cdr.detectChanges();
    this.host.querySelector<HTMLButtonElement>(`[data-day="${d}"]`)?.focus();
  }

  private onOver(ev: Event): void {
    if (this.mode !== 'range' || this.ph() !== 'end') return;
    const b = (ev.target as HTMLElement | null)?.closest?.('[data-day]') as HTMLButtonElement | null;
    const d = b?.getAttribute('data-day');
    if (!b || !d || b.disabled || d === this.hover) return;
    this.hover = d;
    this.rebuild();
    this.cdr.detectChanges();
  }

  private onLeave(): void {
    if (this.hover === null) return;
    this.hover = null;
    this.rebuild();
    this.cdr.detectChanges();
  }

  private setPhase(p: PickerPhase): void {
    this.ownPhase = p;
    if (this.phase !== null) this.phase = p; // l'hôte reprend la valeur par phaseChange
    this.phaseChange.emit(p);
  }

  private setLeft(first: Day): void {
    if (first === this.left) return;
    this.left = first;
    this.leftMonthChange.emit(first);
  }

  private currentLeft(): Day {
    return this.left ?? firstOfMonth(this.today);
  }

  private ph(): PickerPhase {
    return this.mode === 'single' ? 'start' : (this.phase ?? this.ownPhase);
  }

  private startEff(): Day | null {
    return this.cur.start ?? this.startFloor;
  }

  // En jour unique, la phase « fin » sans survol affiche le seul jour choisi, sans plage ouverte.
  private sel(): PickerSel {
    if (this.mode === 'single') return { start: this.cur.start, startEff: this.cur.start, end: null, hover: null, phase: 'end' };
    return { start: this.cur.start, startEff: this.startEff(), end: this.cur.end, hover: this.hover, phase: this.ph() };
  }

  private isDisabled(d: Day): boolean {
    const m = this.marks;
    return !!m && ((m.disabledBefore !== null && d < m.disabledBefore) || (m.disabledFrom !== null && d >= m.disabledFrom));
  }

  private rebuild(): void {
    if (!this.left && !this.today) {
      this.months = [];
      return;
    }
    const left = this.currentLeft();
    const firsts = Array.from({ length: this.monthCount }, (_, i) => addMonthsClamped(left, i));
    const sel = this.sel();
    const tab = this.tabDay(left, addMonthsClamped(left, this.monthCount));
    this.months = firsts.map((first) => {
      const pm = pickerMonthOf(first);
      return { first, title: frMonth(first), blanks: Array.from({ length: pm.blanks }, (_, i) => i), cells: pm.days.map((d) => this.cell(d, sel, tab)) };
    });
  }

  private cell(d: Day, sel: PickerSel, tab: Day | null): PickerCell {
    const f = pickerDayFlags(d, sel);
    const m = this.marks;
    const lock = !!m?.locked.has(d);
    const dot = !!m?.dotted.has(d);
    const cov = m?.covered; // présente : la couverture prend le trait du bas et le cercle pointillé
    const cls = ['pk-day', ...FLAGS.filter((k) => f[k])];
    if (lock && !cov) cls.push('lock');
    if (dot) cls.push('intx');
    if (d === this.today && !cov) cls.push('today');
    if (cov?.has(d)) cls.push('cov');
    else if (m?.partly?.has(d)) cls.push('cov-part');
    if (cov && d === m?.missing) cls.push('miss');
    if (d === this.ringDay) cls.push('ring');
    const tip = m ? m.tip(d) : frWeekday(d);
    // La sélection, que seules les classes montrent, est dite aussi aux lecteurs d'écran.
    const said = [f.st ? (this.mode === 'single' ? 'jour choisi' : 'début') : null, f.en ? 'dernier jour' : null, f.inr ? 'dans la plage' : null, d === this.today ? "aujourd'hui" : null];
    const aria = [tip, ...said.filter((x): x is string => !!x)].join(', ');
    return { d, num: Number(d.slice(8, 10)), cls: cls.join(' '), disabled: this.isDisabled(d), tip, aria, pressed: f.st || f.en, today: d === this.today, lock, dot, tab: d === tab ? 0 : -1 };
  }

  // Un seul jour focalisable : le dernier focalisé, sinon le début, la fin, aujourd'hui, sinon le premier jour actif affiché.
  // start = 1er du mois de gauche ; endExcl = 1er du mois qui suit les deux mois affichés (borne exclue, calcul interne seulement).
  private tabDay(start: Day, endExcl: Day): Day | null {
    const ok = (d: Day | null): d is Day => !!d && d >= start && d < endExcl && !this.isDisabled(d);
    for (const d of [this.focusDay, this.startEff(), this.cur.end, this.today]) if (ok(d)) return d;
    for (let d = start; d < endExcl; d = addDays(d, 1)) if (!this.isDisabled(d)) return d;
    return null;
  }
}
